import { AxiosResponse } from "axios";
import { config } from "../config/app-config";
import HttpsService from "../services/https-service";
import { AuthToken } from "./protocol/login.protocol";
import { QubesUser } from "./model/model";
import { QubesPingRequest, QubesPingResponse } from "./protocol/ping.protocol";
import {
  BIQL,
  BiqlQueryRequest,
  BiqlQueryResponse,
  DataFormats,
} from "./protocol/biql-query.protocol";
import { CrudJson, CrudResponse } from "./protocol/crud.protocol";
import { CdcTable } from "../hermesclient/client";
import {
  PROMPT,
  PromptQueryRequest,
  PromptQueryResponse,
  PromptSseResponse,
} from "./protocol/prompt.protocol";
import { uuidv7 } from "uuidv7";
import { Observable, Subject } from "rxjs";
import { AuthTokenManager } from "../services/auth-token-manager";
import { Duration } from "../duration";
import {
  DefaultDisplayComponent,
  DefaultFormItem,
  DefaultGetValue,
  FieldDef,
} from "../model/qubes/field";

type SQL = string;
type AppSpace = string;

export class QubesAPI {
  // Lazy: qubes is deprecated + optional, so we don't instantiate (which reads
  // config.qubes) until something actually asks for the client. Deployments
  // without qubes never call get() and so never trip the guard below.
  private static _instance: QubesAPI | undefined;

  // baseUrl/publicUrl are only set when qubes is configured; the qubes-only
  // methods (SQL/query) require them via requireQubes(). The auth path (ping)
  // uses config.hermes.url and works without qubes.
  private readonly baseUrl: URL | undefined;
  private readonly publicUrl: string | undefined;
  private authToken: AuthToken | undefined;
  private readonly LOGGA: unknown;

  static get(): QubesAPI {
    if (!this._instance) {
      this._instance = new QubesAPI();
    }
    return this._instance;
  }

  private constructor() {
    // Do NOT require qubes here — the session ping (auth path) only needs
    // config.hermes.url. Only the SQL/query methods need qubes, and they guard
    // via requireQubes() at call time.
    if (config.qubes) {
      this.baseUrl = new URL(config.qubes.apiUrl);
      this.publicUrl = config.qubes.publicUrl;
    }
    this.LOGGA = window.console;
  }

  /** Throws if a qubes-only method is used on a deployment without qubes. */
  private requireQubes(): string {
    if (!this.publicUrl) {
      throw new Error(
        "qubes is not configured for this deployment (config.qubes is unset). " +
          "Set qubes in public/config.js if this deployment still uses qubes.",
      );
    }
    return this.publicUrl;
  }

  setAuthToken(authToken: AuthToken) {
    this.authToken = authToken;
  }

  // public static var Default403Behavior: Void->Void = JQ.noop;

  // static function getDoneFcn(fcn: Dynamic->Void): Dynamic->String->JQXHR->Void {
  //     return function(data: Dynamic, str: String, jqxhr: JQXHR){
  //         if(fcn != null) {
  //             try {
  //                 fcn(data);
  //             } catch (err: Dynamic) {
  //                 LOGGA.error("Error processing success fcn of an ajax call", err);
  //             }
  //         }
  //     };
  // }

  // static function getFailFcn(?failFcn: AjaxException->Void):  JQXHR->String->String->Void {
  //     return function(jqXHR: JQXHR, textStatus: String, errorThrown: String): Void{
  //         var error_message:String = errorThrown;
  //         if (jqXHR.message.isNotBlank()) {
  //             error_message = jqXHR.message;
  //         } else if (jqXHR.responseText.isNotBlank()) {
  //             error_message = jqXHR.responseText;
  //         } else if (error_message.isBlank()){
  //             error_message = "{no error msg from server}";
  //         }
  //         LOGGA.error("API error handler: Status " + jqXHR.status + " | " + error_message);
  //         LOGGA.debug("jqXHR\n--" + haxe.Json.stringify(jqXHR));

  //         if(jqXHR.status == 403 && Default403Behavior != null){
  //             Default403Behavior();
  //         }

  //         var exc: AjaxException = new AjaxException();
  //         exc.message = error_message;
  //         exc.statusCode = jqXHR.status;
  //         if(failFcn != null)
  //             failFcn(exc);
  //     };
  // }

  // static function getCrudFailFcn(?crudFailFcn: CrudResponse->Void):  JQXHR->String->String->Void {
  //     var failFcn = getFailFcn(function(exc: AjaxException): Void {
  //          var crudResponse = {
  //                 errorMessage: exc.message,
  //                 serverStackTrace: null,
  //                 success: false
  //             };
  //         if(crudFailFcn != null)
  //             crudFailFcn(crudResponse);
  //     });
  //     return failFcn;
  // }

  // static function getAlwaysFcn(?alwaysFcn: Dynamic->Void):   Dynamic->String->Dynamic->Void {
  //     return function(dataOrJqXHR: Dynamic, str: String, dyn: Dynamic){
  //         if(alwaysFcn != null)
  //             alwaysFcn(dataOrJqXHR);
  //     };
  // }

  // static function applyHandlers(jqXHR: JQXHR, fcns: AjaxHandlers): Void {
  //     jqXHR.done(getDoneFcn(fcns.done));
  //     jqXHR.fail(getFailFcn(fcns.fail));
  //     jqXHR.always(getAlwaysFcn(fcns.always));
  // }

  // static function applyCrudHandlers(jqXHR: JQXHR, fcns: CrudHandlers): Void {
  //     jqXHR.done(getDoneFcn(fcns.done));
  //     jqXHR.fail(getCrudFailFcn(fcns.fail));
  //     jqXHR.always(getAlwaysFcn(fcns.always));
  // }

  async logout(callbak: (success: boolean) => void): Promise<void> {
    const https = HttpsService.getHttps();
    const authManager = AuthTokenManager.getInstance();

    try {
      await https?.post(`${config.hermes.url}/api/logout`, undefined, {
        headers: {
          ...(this.authToken ? { "X-Auth-Token": this.authToken } : {}),
        },
      });
      authManager.clearToken();
      this.authToken = undefined;
      callbak(true);
    } catch (err) {
      console.error(err);
      authManager.clearToken();
      this.authToken = undefined;
      callbak(false);
    }
  }

  /**
   * Ping the hermes server to confirm the auth token is valid.
   *
   * Expects that the authToken property has been set on the client.
   *
   * @returns instance of `QubesUser` if logged in
   * @throws an error if no authToken property has been set, or if the user is not logged in
   */
  async ping(): Promise<QubesUser> {
    try {
      const https = HttpsService.getHttps();
      const res = await https?.post<
        QubesPingResponse,
        AxiosResponse<QubesPingResponse, QubesPingRequest>,
        QubesPingRequest
      >(
        `${config.hermes.url}/api/ping`,
        {},
        {
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            ...(this.authToken ? { "X-Auth-Token": this.authToken } : {}),
          },
        },
      );

      if (res?.data?.loggedIn) {
        return res.data.user;
      } else {
        throw new Error("User is not logged in");
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  // public static function prettyPrintSql(sql:SQL, htmlResponse:Bool=true, indent:String="\t"):String {
  //  var s:String;
  //        var ajaxOptions: AjaxOptions = Default_Ajax_Opts;
  //        getDefaultAjaxOptions(
  //            function(data: Dynamic, textStatus: String, jqXHR: JQXHR): Void {
  //                s = cast data;
  //            },
  //            function(exc: AjaxException): Void {
  //                LOGGA.error("Pretty Print failed");
  //                s = sql;
  //            }
  //         );
  //        ajaxOptions.async = false;
  //        ajaxOptions.url = baseUrl + "/api/sqlPrettyPrint";
  //        ajaxOptions.data = {
  //                sql: sql,
  //                htmlResponse: htmlResponse,
  //                indent: indent
  //            };
  //        JQ.ajax(ajaxOptions);

  //        return s;
  // }

  // public static function prettyPrintQubql(query:BIQL, htmlResponse:Bool=true):String {
  //     var s:String;
  //     var ajaxOptions: AjaxOptions = getDefaultAjaxOptions(
  //         function(data: Dynamic, textStatus: String, jqXHR: JQXHR): Void {
  //             s = cast data;
  //         },
  //         function(exc: AjaxException): Void {
  //             LOGGA.error("Pretty Print failed");
  //             s = query;
  //         }
  //      );
  //     ajaxOptions.async = false;
  //     ajaxOptions.url = baseUrl + "/api/qubqlPrettyPrint";
  //     ajaxOptions.data = {
  //             query: query,
  //             htmlResponse: htmlResponse
  //         };
  //     JQ.ajax(ajaxOptions);

  //     return s;
  // }

  // public static function kvPut(key: String, json: Any, callbak: Bool->Void): Void {
  //     var ajaxOptions: AjaxOptions = getDefaultAjaxOptions(
  //             function(data: Dynamic, textStatus: String, jqXHR: JQXHR): Void {
  //                 callbak(true);
  //             },
  //             function(exc: AjaxException): Void {
  //                 callbak(false);
  //             }
  //          );
  //     ajaxOptions.url = baseUrl + "/api/kv/" + key;
  //     ajaxOptions.async = false;
  //     ajaxOptions.dataType = "TEXT";
  //     ajaxOptions.contentType = "application/json";
  //     ajaxOptions.data = json;
  //     ajaxOptions.processData = false;

  //     JQ.ajax(ajaxOptions);
  // }

  // public static function kvGet(key: String, callbak: Dynamic->Void): Void {
  //     var ajaxOptions: AjaxOptions = getDefaultAjaxOptions(
  //             function(data: Dynamic, textStatus: String, jqXHR: JQXHR): Void {
  //                 callbak(data);
  //             },
  //             function(exc: AjaxException): Void {

  //             }
  //     );

  //     ajaxOptions.url = baseUrl + "/api/kv/" + key;
  //     ajaxOptions.async = false;
  //     ajaxOptions.dataType = "JSON";
  //     ajaxOptions.type = "GET";
  //     ajaxOptions.contentType = "application/json";

  //     JQ.ajax(ajaxOptions);
  // }

  /**
   * Asynchronously inserts a row based on the specified {@link CrudJson},
   * and expects generic parameters T to specify the object being
   * inserted, and K to specify the keys of the inserted type
   *
   * @param {CrudJson} json - An instance of CrudJson
   * @returns the CrudResponse
   */
  async insertRow<T, K>(json: CrudJson<T>): Promise<CrudResponse<K>> {
    return this._crudOp<T, K>("/api/insert", json);
  }

  /**
   * Asynchronously upserts a row based on the specified {@link CrudJson},
   * and expects generic parameters T to specify the object being
   * inserted, and K to specify the keys of the upserted type
   *
   * @param {CrudJson} json - An instance of CrudJson
   * @returns the CrudResponse
   */
  async upsertRow<T, K>(json: CrudJson<T>): Promise<CrudResponse<K>> {
    return this._crudOp<T, K>("/api/upsertRow", json);
  }

  /**
   * Asynchronously updates a row based on the specified {@link CrudJson},
   * and expects generic parameters T to specify the object being
   * inserted, and K to specify the keys of the updated type
   *
   * @param {CrudJson} json - An instance of CrudJson
   * @returns the CrudResponse
   */
  async updateRow<T, K>(json: CrudJson<T>): Promise<CrudResponse<K>> {
    return this._crudOp<T, K>("/api/updateRow", json);
  }

  /**
   * Asynchronously deletes a row based on the specified {@link CrudJson},
   * and expects generic parameters T to specify the object being
   * deleted, and K to specify the keys of the deleted type
   *
   * @param {CrudJson} json - An instance of CrudJson
   * @returns the CrudResponse
   */
  async deleteRow<T, K>(json: CrudJson<T>): Promise<CrudResponse<K>> {
    return this._crudOp<T, K>("/api/deleteRow", json);
  }

  private async _crudOp<T, K>(
    url: string,
    json: CrudJson<T>,
  ): Promise<CrudResponse<K>> {
    if (!json.appSpace) {
      throw new Error("AppSpace must be specified");
    }

    try {
      const https = HttpsService.getHttps();
      const res = await https?.post<
        CrudResponse<K>,
        AxiosResponse<CrudResponse<K>, CrudJson<T>>,
        CrudJson<T>
      >(`${this.requireQubes()}${url}`, json, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...(this.authToken ? { "X-Auth-Token": this.authToken } : {}),
        },
      });
      return res?.data;
    } catch (err) {
      //handle the error
      console.error(err);
      throw err;
    }
  }

  // public static function ping(?handlers: AjaxHandlers): Dynamic {
  //     var ajaxOptions: AjaxOptions = Default_Ajax_Opts;
  //     ajaxOptions.url = baseUrl + "/api/ping";

  //     var jqXHR: JQXHR = JQ.ajax(ajaxOptions);
  //     applyHandlers(jqXHR, handlers);
  //     return jqXHR;
  // }

  async biqlQuery<T>(
    query: BIQL,
    appSpace?: AppSpace,
    dataFormat?: DataFormats,
  ): Promise<BiqlQueryResponse<T>> {
    const json: BiqlQueryRequest = {
      query: query,
      dataFormat: dataFormat ?? "concise",
    };
    if (appSpace) json.appSpace = appSpace;

    try {
      const https = HttpsService.getHttps();
      const res = await https?.post<
        BiqlQueryResponse<T>,
        AxiosResponse<BiqlQueryResponse<T>, BiqlQueryRequest>,
        BiqlQueryRequest
      >(`${this.requireQubes()}/api/query`, json, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...(this.authToken ? { "X-Auth-Token": this.authToken } : {}),
        },
      });
      return res?.data;
    } catch (err) {
      //handle the error
      console.error(err);
      throw err;
    }
  }

  /**
   * Send a prompt to the AI server
   * @param {SQL | PROMPT} query - The SQL or prompt query to send
   * @returns The response from the AI server
   */
  async sqlQueryPrompt(query: SQL | PROMPT): Promise<PromptQueryResponse> {
    const json: PromptQueryRequest = {
      route: ":databaseid:base",
      query: `prompt ${query}`,
      mode: "readonly",
      uid: uuidv7().replaceAll("-", ""),
    };

    try {
      const https = HttpsService.getHttps();
      const res = await https?.post<
        PromptQueryRequest,
        AxiosResponse<PromptQueryResponse, PromptQueryRequest>,
        PromptQueryRequest
      >(`${this.requireQubes()}/api/sql/query`, json, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...(this.authToken ? { "X-Auth-Token": this.authToken } : {}),
        },
      });
      return res?.data;
    } catch (err) {
      //handle the error
      console.error(err);
      throw err;
    }
  }

  /**
   * Call the prompt API's SSE endpoint, and return an Observable
   */
  promptSse(query: SQL | PROMPT): Observable<PromptSseResponse> {
    const subject = new Subject<PromptSseResponse>();
    const https = HttpsService.getHttps();

    https
      ?.get<
        PromptQueryRequest,
        AxiosResponse<ReadableStream, PromptQueryRequest>,
        PromptQueryRequest
      >(
        `${this.requireQubes()}/api/mcp/runPrompt?prompt=${encodeURIComponent(query)}`,
        {
          responseType: "stream",
          headers: {
            Accept: "text/event-stream",
            ...(this.authToken ? { "X-Auth-Token": this.authToken } : {}),
          },
          adapter: "fetch",
        },
      )
      .then(async (res) => {
        const stream = res.data as ReadableStream;
        const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

        let buffer = "";
        let sseResponseMsg: PromptSseResponse = {
          event: "",
          data: "",
          dataJson: undefined,
        };
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += value;
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("event:")) {
              const eventType = line.substring(6)?.trim(); // Remove "event:" prefix
              // Process your SSE event type here
              // console.log("Received SSE event:", eventType);
              sseResponseMsg.event = eventType;
              continue;
            }
            if (line.startsWith("data:")) {
              const eventData = line.substring(5)?.trim(); // Remove "data:" prefix
              // Process your SSE event data here
              // create a 50 character summary
              // const summary = eventData.substring(0, 50);
              // console.log("Received SSE:", summary);
              sseResponseMsg.data = eventData;
              if (sseResponseMsg.event !== "complete") {
                try {
                  sseResponseMsg.dataJson = JSON.parse(sseResponseMsg.data);
                } catch (error) {
                  console.error("Error parsing SSE data:", error);
                }
              } else {
                // we've received the "complete" message
                subject.complete();
                break;
              }
              subject.next(sseResponseMsg);
              sseResponseMsg = {
                event: "",
                data: "",
                dataJson: undefined,
              };
              continue;
            }
          }
        }
        // if there are no more lines to read, mark the subject as complete
        subject.complete();
      })
      .catch((err) => {
        console.error(err);
        subject.error(err);
      });

    return subject.asObservable();
  }

  // public static function multiRequest(json: Array<MethodInvocation>, handlers: AjaxHandlers): JQXHR {
  //     var ajaxOptions: AjaxOptions = Default_Ajax_Opts;
  //     ajaxOptions.url = baseUrl + "/api/multirequest";
  //     ajaxOptions.data = Json.stringify(json);

  //     var jqXHR: JQXHR = JQ.ajax(ajaxOptions);
  //     applyHandlers(jqXHR, handlers);
  //     return jqXHR;
  // }
}

/**
 * Get the default implementation of BiqlQuery
 * @returns {BiqlQuery} an instance of BiqlQuery
 */
export function GetBiqlQueryImpl(): BiqlQuery {
  return new BiqlQueryImpl();
}

export interface BiqlQuery {
  from(appSpace: string, cube: string): BiqlQuery;
  changeDataCapture(database: string, table: string, pk: string): BiqlQuery;
  field(field: string | BiqlField): BiqlQuery;
  calculatedField(field: CalculatedField): BiqlQuery;
  where(conditionExpr: string): BiqlQuery;
  clone(): BiqlQuery;
  appendWhere(conditionExpr: string): BiqlQuery;
  appendSort(sortExpr: string): BiqlQuery;
  appendLimit(limit: number): BiqlQuery;
  refreshInterval(duration: Duration): BiqlQuery;

  changeDataCaptureArgs(): CdcArgs | undefined;
  getRefreshInterval(): Duration | undefined;

  appSpace(): string;
  cube(): string;
  getWhere(): string | undefined;
  getSort(): string | undefined;
  getLimit(): number | undefined;
  buildQueryString(): string;
  fields(): Array<BiqlField>;
  calculatedFields(): Array<CalculatedField>;
}

export interface CalculatedField extends FieldDef {
  align?: "left" | "center" | "right";
}

export interface BiqlField extends FieldDef {
  expr?: string;
  primaryKey?: boolean;
  hidden?: boolean;
  /**
   * The view still works without this column: when the live firewall model
   * lacks one (a UI newer than its server), the grid retries without its
   * optional columns, renders them blank and shows a warning. A string names
   * the shown column that goes blank (a hidden source field's own name means
   * nothing to the person reading the warning).
   */
  optional?: boolean | string;
  alias?: string;
  foreignCdc?: ForeignKeyChangeDataCapture;
}

export interface ForeignKeyChangeDataCapture {
  table: string;
  pk: string;
}

interface CdcArgs {
  table: CdcTable;
  pk: string;
}

class BiqlQueryImpl implements BiqlQuery {
  appSpace_?: string;
  cube_?: string;
  cdc?: CdcArgs;

  fields_ = new Array<BiqlField>();
  calculatedFields_ = new Array<CalculatedField>();
  where_?: string;
  sort_?: string;
  limit_?: number;
  refreshInterval_?: Duration;

  constructor() {}

  fields(): BiqlField[] {
    return this.fields_;
  }

  from(appSpace: string, cube: string): BiqlQuery {
    this.appSpace_ = appSpace;
    this.cube_ = cube;
    return this;
  }

  changeDataCapture(database: string, table: string, pk: string): BiqlQuery {
    this.cdc = {
      table: {
        database: database,
        table: table,
      },
      pk: pk,
    };
    return this;
  }

  field(field: string | BiqlField): BiqlQuery {
    if (typeof field === "string") {
      this.fields_.push({
        name: field,
        type: "string",
        getValue: DefaultGetValue,
        getDisplayComponent: DefaultDisplayComponent,
        getFormItem: DefaultFormItem,
      });
    } else {
      this.fields_.push(field);
    }
    return this;
  }

  calculatedField(field: CalculatedField): BiqlQuery {
    this.calculatedFields_.push(field);
    return this;
  }

  calculatedFields(): Array<CalculatedField> {
    return this.calculatedFields_;
  }

  where(conditionExpr: string): BiqlQuery {
    this.where_ = conditionExpr;
    return this;
  }

  clone(): BiqlQuery {
    const c = new BiqlQueryImpl();
    c.appSpace_ = this.appSpace_;
    c.cdc = this.cdc;
    c.cube_ = this.cube_;
    c.where_ = this.where_;
    c.sort_ = this.sort_;
    c.limit_ = this.limit_;
    c.refreshInterval_ = this.refreshInterval_;

    c.fields_ = [];
    this.fields_.forEach((f) => c.fields_.push(f));
    return c;
  }

  /**
   * Get the app space name (if supplied)
   * @returns the app space name
   */
  appSpace(): string {
    if (this.appSpace_) {
      return this.appSpace_;
    }
    throw new Error("appSpace must be supplied");
  }

  /**
   * Get the cube name (if supplied)
   * @returns the cube name
   */
  cube(): string {
    if (this.cube_) {
      return this.cube_;
    }
    throw new Error("cube must be supplied");
  }

  getWhere(): string | undefined {
    return this.where_;
  }

  getSort(): string | undefined {
    return this.sort_;
  }

  getLimit(): number | undefined {
    return this.limit_;
  }

  changeDataCaptureArgs(): CdcArgs | undefined {
    return this.cdc;
  }

  buildQueryString(): string {
    const selectFields = this.fields_
      .map((field) => {
        return field.name;
      })
      .join(", ");
    const whereClause = this.where_ ? ` where ${this.where_}` : "";
    const sortClause = this.sort_ ? ` order by ${this.sort_}` : "";
    const limitClause = this.limit_ ? ` limit ${this.limit_}` : "";
    return `from ${this.appSpace()}/${this.cube()} select ${selectFields}${whereClause}${sortClause}${limitClause}`;
  }

  appendWhere(conditionExpr: string): BiqlQuery {
    if (this.where_) {
      this.where_ = `(${this.where_}) and (${conditionExpr})`;
    } else {
      this.where_ = conditionExpr;
    }
    return this;
  }

  appendSort(sortExpr: string): BiqlQuery {
    if (this.sort_) {
      this.sort_ = `${this.sort_}, ${sortExpr}`;
    } else {
      this.sort_ = sortExpr;
    }
    return this;
  }

  appendLimit(limit: number): BiqlQuery {
    this.limit_ = limit;
    return this;
  }

  refreshInterval(duration: Duration): BiqlQuery {
    this.refreshInterval_ = duration;
    return this;
  }

  getRefreshInterval(): Duration | undefined {
    return this.refreshInterval_;
  }
}
